import { useState, useEffect, useRef } from 'react';
import { api, getCurrentMonth, formatCurrency } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ChefHat, Plus, X, Trash2, ChevronLeft, ChevronRight, CreditCard, Banknote, Check, RefreshCw, Loader2, FileText, ExternalLink, Users, ShoppingBag, AlertTriangle, Clock, ChevronDown, ChevronUp, Camera, ScanLine } from 'lucide-react';
import { toast } from 'sonner';
import { compressImage } from '../../components/shared/FileUpload';

const MONTHS_FR = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const Traiteur = () => {
  const [mois, setMois] = useState(getCurrentMonth());
  const [activeTab, setActiveTab] = useState('axonaut');
  const [axonautData, setAxonautData] = useState(null);
  const [axonautStats, setAxonautStats] = useState(null);
  const [pendingData, setPendingData] = useState(null);
  const [ventes, setVentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [expandedInvoice, setExpandedInvoice] = useState(null);
  const [form, setForm] = useState({ date_evenement: '', client: '', description: '', montant_ht: '', mode_paiement: 'cb' });
  const [scanning, setScanning] = useState(false);
  const fileInputRef = useRef(null);

  const scanFactureVente = async (files) => {
    if (!files || files.length === 0) return;
    setScanning(true);
    try {
      const fd = new FormData();
      for (const f of files) {
        const compressed = await compressImage(f);
        fd.append('files', compressed);
      }
      const res = await api.post('/traiteur/scan-facture-vente?auto_save=true', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      if (res.data.status === 'duplicate') {
        toast.warning('Cette facture existe déjà');
      } else if (res.data.status === 'saved') {
        const v = res.data.vente;
        toast.success(`${v.client} — ${formatCurrency(v.total_ttc)} TTC enregistrée`);
        fetchAll();
      } else {
        toast.info('Facture analysée mais pas enregistrée (montant 0)');
      }
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors du scan');
    } finally {
      setScanning(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [axRes, ventesRes, statsRes, pendingRes] = await Promise.all([
        api.get(`/axonaut/invoices?mois=${mois}`),
        api.get(`/traiteur?mois=${mois}`),
        api.get(`/axonaut/stats/${mois}`),
        api.get('/axonaut/pending'),
      ]);
      setAxonautData(axRes.data);
      setVentes(ventesRes.data);
      setAxonautStats(statsRes.data);
      setPendingData(pendingRes.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, [mois]);

  const navigateMonth = (dir) => {
    const [y, m] = mois.split('-').map(Number);
    let nm = m + dir, ny = y;
    if (nm > 12) { nm = 1; ny++; }
    if (nm < 1) { nm = 12; ny--; }
    setMois(`${ny}-${String(nm).padStart(2, '0')}`);
  };

  const syncAxonaut = async () => {
    setSyncing(true);
    try {
      const res = await api.post(`/axonaut/sync?mois=${mois}`);
      toast.success(`${res.data.nb_ajoutees} ajoutées, ${res.data.nb_mises_a_jour} mises à jour`);
      fetchAll();
    } catch { toast.error('Erreur sync'); }
    setSyncing(false);
  };

  const syncAll = async () => {
    setSyncingAll(true);
    try {
      const res = await api.post('/axonaut/sync-all');
      toast.success(`${res.data.total} factures synchronisées`);
      fetchAll();
    } catch { toast.error('Erreur sync complète'); }
    setSyncingAll(false);
  };

  const submitVente = async () => {
    if (!form.client || !form.montant_ht) {
      toast.error('Remplir client et montant');
      return;
    }
    const montantHt = parseFloat(form.montant_ht) || 0;
    const montantTtc = Math.round(montantHt * 1.1 * 100) / 100;
    try {
      await api.post('/traiteur', {
        date_evenement: form.date_evenement || new Date().toISOString().split('T')[0],
        client: form.client,
        description: form.description,
        lignes: [{ description: form.description || 'Traiteur', montant_ttc: montantTtc, taux_tva: 10 }],
        acompte: montantTtc,
        mode_paiement: form.mode_paiement
      });
      toast.success(`${form.client} — ${formatCurrency(montantHt)} HT`);
      setForm({ date_evenement: '', client: '', description: '', montant_ht: '', mode_paiement: 'cb' });
      setShowForm(false);
      fetchAll();
    } catch { toast.error('Erreur'); }
  };

  const deleteVente = async (id) => {
    try {
      await api.delete(`/traiteur/${id}`);
      toast.success('Supprimé');
      fetchAll();
    } catch { toast.error('Erreur'); }
  };

  const [y, m] = mois.split('-').map(Number);
  const stats = axonautData?.stats || {};
  const invoices = axonautData?.invoices || [];

  const tabs = [
    { id: 'axonaut', label: 'Axonaut' },
    { id: 'attente', label: `En attente${pendingData ? ` (${pendingData.nb_total})` : ''}` },
    { id: 'manuel', label: 'Saisie manuelle' },
  ];

  const formatDate = (d) => {
    if (!d) return '';
    const parts = d.split('-');
    if (parts.length === 3) return `${parts[2]}/${parts[1]}`;
    return d;
  };

  return (
    <div className="space-y-4" data-testid="traiteur-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-[rgb(var(--lm-text))] flex items-center gap-2">
          <ChefHat size={20} className="text-lemas-gold" /> Traiteur
        </h2>
        <div className="flex items-center gap-2">
          <button onClick={() => navigateMonth(-1)} className="p-1.5 rounded-lg hover:bg-[rgb(var(--lm-surface))]">
            <ChevronLeft size={16} className="text-[rgb(var(--lm-text-muted))]" />
          </button>
          <span className="text-sm font-medium text-[rgb(var(--lm-text))] min-w-[120px] text-center">
            {MONTHS_FR[m]} {y}
          </span>
          <button onClick={() => navigateMonth(1)} className="p-1.5 rounded-lg hover:bg-[rgb(var(--lm-surface))]">
            <ChevronRight size={16} className="text-[rgb(var(--lm-text-muted))]" />
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="lm-card p-4 text-center">
          <p className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))]">CA HT</p>
          <p className="font-mono text-xl text-lemas-gold mt-1" data-testid="ca-traiteur">{formatCurrency(stats.total_ht || 0)}</p>
        </div>
        <div className="lm-card p-4 text-center">
          <p className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))]">Factures</p>
          <p className="font-mono text-xl text-[rgb(var(--lm-text))] mt-1">{stats.nb_factures || 0}</p>
        </div>
        <div className="lm-card p-4 text-center">
          <p className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))]">Payées</p>
          <p className="font-mono text-xl text-[rgb(var(--lm-green))] mt-1">{stats.nb_payees || 0}</p>
        </div>
        <div className="lm-card p-4 text-center">
          <p className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))]">Impayés</p>
          <p className={`font-mono text-xl mt-1 ${(stats.nb_impayees || 0) > 0 ? 'text-[rgb(var(--lm-red))]' : 'text-[rgb(var(--lm-green))]'}`}
            data-testid="impayes">{formatCurrency(stats.total_impaye || 0)}</p>
        </div>
      </div>

      {/* Global pending alert */}
      {pendingData && pendingData.nb_retard > 0 && (
        <div className="lm-card p-3 border border-[rgb(var(--lm-red)/0.3)] bg-[rgb(var(--lm-red)/0.05)]" data-testid="alert-retard">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-[rgb(var(--lm-red))]" />
              <span className="text-sm text-[rgb(var(--lm-red))] font-medium">
                {pendingData.nb_retard} facture{pendingData.nb_retard > 1 ? 's' : ''} en retard — {formatCurrency(pendingData.total_retard)} TTC
              </span>
            </div>
            <button onClick={() => setActiveTab('attente')} className="text-xs text-[rgb(var(--lm-red))] underline hover:no-underline">
              Voir
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl bg-[rgb(var(--lm-bg))]">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all ${
              activeTab === t.id ? 'bg-[rgb(var(--lm-card))] text-lemas-gold shadow-sm' : 'text-[rgb(var(--lm-text-muted))]'
            }`} data-testid={`tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Axonaut Tab */}
      {activeTab === 'axonaut' && (
        <div className="space-y-4 animate-fade-in">
          <div className="flex gap-2 flex-wrap">
            <Button onClick={syncAxonaut} disabled={syncing || syncingAll} size="sm"
              className="bg-lemas-gold text-[rgb(var(--lm-bg))] hover:bg-lemas-gold/90 text-xs" data-testid="sync-btn">
              {syncing ? <Loader2 size={14} className="mr-1 animate-spin" /> : <RefreshCw size={14} className="mr-1" />}
              Sync ce mois
            </Button>
            <Button onClick={syncAll} disabled={syncing || syncingAll} size="sm" variant="outline"
              className="text-xs border-[rgb(var(--lm-border))]" data-testid="sync-all-btn">
              {syncingAll ? <Loader2 size={14} className="mr-1 animate-spin" /> : <RefreshCw size={14} className="mr-1" />}
              Sync tout
            </Button>
          </div>

          {/* Top clients & products */}
          {axonautStats && (axonautStats.top_clients?.length > 0 || axonautStats.top_produits?.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {axonautStats.top_clients?.length > 0 && (
                <div className="lm-card p-4">
                  <h3 className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))] mb-2 flex items-center gap-1">
                    <Users size={10} /> Top clients
                  </h3>
                  <div className="space-y-1.5">
                    {axonautStats.top_clients.map((c, i) => (
                      <div key={i} className="flex justify-between text-xs">
                        <span className="text-[rgb(var(--lm-text-secondary))]">{c.client}</span>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-lemas-gold">{formatCurrency(c.total_ht)}</span>
                          <span className="text-[rgb(var(--lm-text-muted))]">({c.nb})</span>
                          {c.impaye > 0 && (
                            <span className="text-[10px] text-[rgb(var(--lm-red))]">{formatCurrency(c.impaye)} dû</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {axonautStats.top_produits?.length > 0 && (
                <div className="lm-card p-4">
                  <h3 className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))] mb-2 flex items-center gap-1">
                    <ShoppingBag size={10} /> Produits vendus
                  </h3>
                  <div className="space-y-1.5">
                    {axonautStats.top_produits.map((p, i) => (
                      <div key={i} className="flex justify-between text-xs">
                        <span className="text-[rgb(var(--lm-text-secondary))] truncate max-w-[60%]">{p.produit}</span>
                        <span className="font-mono text-[rgb(var(--lm-green))]">{formatCurrency(p.total_ht)} <span className="text-[rgb(var(--lm-text-muted))]">(x{p.quantite})</span></span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Marge if available */}
          {axonautStats?.total_marge > 0 && (
            <div className="lm-card p-3 flex items-center justify-between">
              <span className="text-xs text-[rgb(var(--lm-text-muted))]">Marge totale du mois</span>
              <span className="font-mono text-sm text-[rgb(var(--lm-green))]">{formatCurrency(axonautStats.total_marge)}</span>
            </div>
          )}

          {/* Invoices list */}
          <div className="lm-card overflow-hidden">
            <div className="divide-y divide-[rgb(var(--lm-border))]">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="animate-spin text-lemas-gold" size={20} />
                </div>
              ) : invoices.length > 0 ? invoices.map((inv, i) => (
                <div key={i} data-testid={`invoice-${i}`}>
                  <div className="px-4 py-3 hover:bg-[rgb(var(--lm-bg)/0.3)] cursor-pointer"
                    onClick={() => setExpandedInvoice(expandedInvoice === i ? null : i)}>
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-[rgb(var(--lm-text))] truncate">{inv.client}</p>
                          {inv.en_retard && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[rgb(var(--lm-red)/0.15)] text-[rgb(var(--lm-red))] flex items-center gap-0.5 whitespace-nowrap">
                              <AlertTriangle size={8} /> Retard
                            </span>
                          )}
                          {!inv.paye && !inv.en_retard && inv.impaye > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[rgb(var(--lm-gold)/0.15)] text-lemas-gold flex items-center gap-0.5 whitespace-nowrap">
                              <Clock size={8} /> En attente
                            </span>
                          )}
                          {inv.paye && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[rgb(var(--lm-green)/0.1)] text-[rgb(var(--lm-green))]">
                              <Check size={8} className="inline" /> Payé
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] text-[rgb(var(--lm-text-muted))]">{inv.numero}</span>
                          <span className="text-[10px] text-[rgb(var(--lm-text-muted))]">{formatDate(inv.date)}</span>
                          {inv.date_echeance && (
                            <span className={`text-[10px] ${inv.en_retard ? 'text-[rgb(var(--lm-red))]' : 'text-[rgb(var(--lm-text-muted))]'}`}>
                              Ech. {formatDate(inv.date_echeance)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="text-right">
                          <p className="font-mono text-sm text-lemas-gold">{formatCurrency(inv.total_ht)} HT</p>
                          <p className="font-mono text-[10px] text-[rgb(var(--lm-text-muted))]">{formatCurrency(inv.total_ttc)} TTC</p>
                        </div>
                        <div className="flex items-center gap-1">
                          {inv.pdf_url && (
                            <a href={inv.pdf_url} target="_blank" rel="noopener noreferrer"
                              className="p-1.5 rounded hover:bg-[rgb(var(--lm-bg))]"
                              onClick={e => e.stopPropagation()}>
                              <FileText size={14} className="text-[rgb(var(--lm-text-muted))]" />
                            </a>
                          )}
                          {inv.portail_url && (
                            <a href={inv.portail_url} target="_blank" rel="noopener noreferrer"
                              className="p-1.5 rounded hover:bg-[rgb(var(--lm-bg))]"
                              onClick={e => e.stopPropagation()}>
                              <ExternalLink size={14} className="text-[rgb(var(--lm-text-muted))]" />
                            </a>
                          )}
                          {expandedInvoice === i ? <ChevronUp size={14} className="text-[rgb(var(--lm-text-muted))]" /> : <ChevronDown size={14} className="text-[rgb(var(--lm-text-muted))]" />}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Expanded detail */}
                  {expandedInvoice === i && inv.lignes?.length > 0 && (
                    <div className="px-4 pb-3 bg-[rgb(var(--lm-bg)/0.3)]">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-[rgb(var(--lm-text-muted))]">
                            <th className="text-left py-1 font-normal">Produit</th>
                            <th className="text-right py-1 font-normal">Qté</th>
                            <th className="text-right py-1 font-normal">PU HT</th>
                            <th className="text-right py-1 font-normal">Total HT</th>
                            <th className="text-right py-1 font-normal">TVA</th>
                          </tr>
                        </thead>
                        <tbody>
                          {inv.lignes.map((l, j) => (
                            <tr key={j} className="text-[rgb(var(--lm-text-secondary))]">
                              <td className="py-1">
                                <span>{l.produit}</span>
                                {l.chapitre && <span className="text-[rgb(var(--lm-text-muted))] ml-1 text-[10px]">({l.chapitre})</span>}
                              </td>
                              <td className="text-right py-1 font-mono">{l.quantite}</td>
                              <td className="text-right py-1 font-mono">{formatCurrency(l.prix_unitaire_ht)}</td>
                              <td className="text-right py-1 font-mono text-lemas-gold">{formatCurrency(l.total_ht)}</td>
                              <td className="text-right py-1 font-mono">{l.tva}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )) : (
                <p className="text-sm text-[rgb(var(--lm-text-muted))] text-center py-8">Aucune facture Axonaut pour ce mois</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* En attente Tab */}
      {activeTab === 'attente' && (
        <div className="space-y-4 animate-fade-in">
          {pendingData && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                <div className="lm-card p-4 text-center">
                  <p className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))]">Total impayé</p>
                  <p className="font-mono text-xl text-[rgb(var(--lm-red))] mt-1" data-testid="total-impaye">
                    {formatCurrency(pendingData.total_impaye)}
                  </p>
                </div>
                <div className="lm-card p-4 text-center">
                  <p className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))]">En retard</p>
                  <p className="font-mono text-xl text-[rgb(var(--lm-red))] mt-1">{pendingData.nb_retard}</p>
                </div>
                <div className="lm-card p-4 text-center">
                  <p className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))]">Factures</p>
                  <p className="font-mono text-xl text-[rgb(var(--lm-text))] mt-1">{pendingData.nb_total}</p>
                </div>
              </div>

              <div className="lm-card overflow-hidden divide-y divide-[rgb(var(--lm-border))]">
                {pendingData.invoices.length > 0 ? pendingData.invoices.map((inv, i) => (
                  <div key={i} className="px-4 py-3 hover:bg-[rgb(var(--lm-bg)/0.3)]" data-testid={`pending-${i}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-[rgb(var(--lm-text))]">{inv.client}</p>
                          {inv.en_retard && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[rgb(var(--lm-red)/0.15)] text-[rgb(var(--lm-red))] font-medium">
                              {inv.jours_retard}j de retard
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] text-[rgb(var(--lm-text-muted))]">{inv.numero}</span>
                          <span className="text-[10px] text-[rgb(var(--lm-text-muted))]">du {formatDate(inv.date)}</span>
                          <span className="text-[10px] text-[rgb(var(--lm-red))]">Éch. {formatDate(inv.date_echeance)}</span>
                        </div>
                        {inv.lignes?.length > 0 && (
                          <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-0.5">
                            {inv.lignes.map(l => l.produit).join(', ')}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="text-right">
                          <p className="font-mono text-sm text-[rgb(var(--lm-red))]">{formatCurrency(inv.impaye)} TTC</p>
                          <p className="font-mono text-[10px] text-[rgb(var(--lm-text-muted))]">{formatCurrency(inv.total_ht)} HT</p>
                        </div>
                        {inv.portail_url && (
                          <a href={inv.portail_url} target="_blank" rel="noopener noreferrer"
                            className="p-1.5 rounded hover:bg-[rgb(var(--lm-bg))] text-[rgb(var(--lm-text-muted))] hover:text-lemas-gold transition-colors"
                            title="Portail client">
                            <ExternalLink size={14} />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                )) : (
                  <p className="text-sm text-[rgb(var(--lm-green))] text-center py-8">Aucune facture en attente</p>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Manuel Tab */}
      {activeTab === 'manuel' && (
        <div className="space-y-4 animate-fade-in">
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => setShowForm(!showForm)} size="sm" variant="outline"
              className="text-xs border-[rgb(var(--lm-border))]" data-testid="add-manual-btn">
              <Plus size={14} className="mr-1" /> Saisie manuelle
            </Button>
            <Button
              onClick={() => fileInputRef.current?.click()}
              size="sm"
              disabled={scanning}
              className="text-xs bg-lemas-gold text-[rgb(var(--lm-bg))] hover:bg-lemas-gold/90"
              data-testid="scan-facture-vente-btn"
            >
              {scanning ? <Loader2 size={14} className="mr-1 animate-spin" /> : <ScanLine size={14} className="mr-1" />}
              {scanning ? 'Analyse...' : 'Scanner facture de vente'}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              multiple
              className="hidden"
              onChange={(e) => scanFactureVente(e.target.files)}
              data-testid="scan-facture-vente-input"
            />
          </div>

          {showForm && (
            <div className="lm-card p-4 animate-slide-up space-y-3" data-testid="manual-form">
              <div className="flex justify-between items-center">
                <h3 className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))]">Nouvelle vente</h3>
                <button onClick={() => setShowForm(false)}><X size={14} className="text-[rgb(var(--lm-text-muted))]" /></button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-[rgb(var(--lm-text-muted))] block mb-1">Date</label>
                  <Input type="date" value={form.date_evenement} onChange={e => setForm(f => ({ ...f, date_evenement: e.target.value }))} className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]" />
                </div>
                <div>
                  <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Client</label>
                  <Input value={form.client} onChange={e => setForm(f => ({ ...f, client: e.target.value }))} placeholder="Nom du client" className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]" data-testid="traiteur-client" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Description</label>
                  <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Menu, buffet..." className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]" />
                </div>
                <div>
                  <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Montant HT</label>
                  <Input type="number" value={form.montant_ht} onChange={e => setForm(f => ({ ...f, montant_ht: e.target.value }))} placeholder="0" className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] font-mono" data-testid="traiteur-montant" />
                </div>
              </div>
              <div className="flex gap-2">
                {[{ v: 'cb', i: CreditCard, l: 'CB' }, { v: 'especes', i: Banknote, l: 'Espèces' }, { v: 'virement', i: Check, l: 'Virement' }].map(pm => (
                  <button key={pm.v} onClick={() => setForm(f => ({ ...f, mode_paiement: pm.v }))}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs transition-all ${form.mode_paiement === pm.v ? 'bg-lemas-gold/20 text-lemas-gold' : 'bg-[rgb(var(--lm-surface))] text-[rgb(var(--lm-text-muted))]'}`}>
                    <pm.i size={12} /> {pm.l}
                  </button>
                ))}
              </div>
              <Button onClick={submitVente} className="w-full bg-lemas-gold text-[rgb(var(--lm-bg))]" data-testid="submit-vente">
                Enregistrer
              </Button>
            </div>
          )}

          {/* Manual entries */}
          <div className="lm-card overflow-hidden divide-y divide-[rgb(var(--lm-border))]">
            {ventes.filter(v => !v.axonaut_id).length > 0 ? ventes.filter(v => !v.axonaut_id).map((v, i) => (
              <div key={i} className="px-4 py-3 flex items-center justify-between hover:bg-[rgb(var(--lm-bg)/0.3)]">
                <div>
                  <p className="text-sm font-medium text-[rgb(var(--lm-text))]">{v.client}</p>
                  <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{v.date_evenement} · {v.description || ''}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm text-lemas-gold">{formatCurrency(v.total_ht || 0)} HT</span>
                  <button onClick={() => deleteVente(v.id)} className="p-1 rounded hover:bg-[rgb(var(--lm-bg))] text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-red))]">
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            )) : (
              <p className="text-sm text-[rgb(var(--lm-text-muted))] text-center py-6">Aucune saisie manuelle</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Traiteur;
